/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AlertDialog from "../components/common/AlertDialog";

const SessionExpiryContext = createContext(null);

export function SessionExpiryProvider({ children, loginPath = "/sign-in" }) {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null); // optional detail from server
  const [title, setTitle] = useState("Session expired");
  const [message, setMessage] = useState("Your session has expired. Please login again to continue.");
  const navigate = useNavigate();
  const location = useLocation();

  // Show dialog (call from code)
  const show = useCallback((opts = {}) => {
    setTitle(opts.title || "Session expired");
    setMessage(opts.message || "Your session has expired. Please login again to continue.");
    setPayload(opts.detail || null);
    setOpen(true);
  }, []);

  const hide = useCallback(() => {
    setOpen(false);
    setPayload(null);
  }, []);

  // Continue button handler -> redirect to login with next param
  const handleContinue = useCallback(() => {
    hide();
    // preserve current path for redirect after login
    const next = location.pathname + (location.search || "");
    navigate(`${loginPath}?next=${encodeURIComponent(next)}`);
  }, [hide, navigate, location, loginPath]);

  // Listen for global event dispatched by API layer (window.dispatchEvent)
  useEffect(() => {
    const onSessionExpired = (ev) => {
      const detail = ev?.detail ?? null;
      // optional: customize title/message using server detail
      const title = (detail && detail.title) || "Session expired";
      const message = (detail && detail.message) || "Your session has expired. Please login again to continue.";
      show({ title, message, detail });
    };

    window.addEventListener("session-expired", onSessionExpired);
    return () => window.removeEventListener("session-expired", onSessionExpired);
  }, [show]);

  return (
    <SessionExpiryContext.Provider value={{ show, hide, open, payload }}>
      {children}

      {/* Centralized dialog using your AlertDialog component */}
      <AlertDialog
        open={open}
        title={title}
        message={message}
        type="error"
        okLabel="Continue"
        showCancel={false}
        onOk={handleContinue}
        mini_mode={true}
      />
    </SessionExpiryContext.Provider>
  );
}

// hook for components to trigger dialog manually (rarely needed)
export function useSessionExpiry() {
  const ctx = useContext(SessionExpiryContext);
  if (!ctx) throw new Error("useSessionExpiry must be used inside a SessionExpiryProvider");
  return ctx;
}
